import React, { createContext, ReactElement, useCallback, useState } from 'react';
import { sendEventTracking } from 'src/components/Analytics';
import { useProductContext } from 'src/hooks/ProductContextHooks';
import { useTrackingContext } from 'src/hooks/TrackingContextHooks';
import { UPLOAD_UNIFIED_EVENT, UploadSteps } from 'src/utils/Constants';
import { text } from 'src/utils/localizationUtil';
import { createTrackingDataForProduct } from 'src/utils/trackingUtil';

export interface ErrorContextProps {
  error: Error | null;
  setError: (error: unknown) => void;
}

export const ErrorContext = createContext<ErrorContextProps>({
  error: null,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setError: (error: unknown /*Whenever you catch something in typescript, it is always of type unknown/any :/ */) => {
    return;
  },
});

export interface ErrorContextProviderProps {
  children?: React.ReactNode;
}

export const ErrorContextProvider = ({ children }: ErrorContextProviderProps): ReactElement => {
  const [error, setErrorInternal] = useState<Error | null>(null);
  const { trackingConfiguration } = useTrackingContext();
  const { productInformation } = useProductContext();

  const setErrorAndScroll = useCallback(
    (error: unknown) => {
      if (!error) {
        setErrorInternal(null);
        return;
      }

      setErrorInternal(error as Error); //Whenever you catch something in typescript, it's always of type unknown, because you can throw literally anything

      window.scrollTo(0, 0);

      if (trackingConfiguration && productInformation.mpvId) {
        sendEventTracking({
          ...trackingConfiguration,
          ...createTrackingDataForProduct({
            mpvId: productInformation.mpvId,
            productKey: productInformation.productKey,
            productName: productInformation.productName,
            productVersion: productInformation.productVersion,
          }),
          action: UPLOAD_UNIFIED_EVENT,
          uploadStep: UploadSteps.ValidationFailed,
          category: 'Design - Upload and Print',
          label: text('error-message'),
        });
      }
    },
    [
      trackingConfiguration,
      productInformation.mpvId,
      productInformation.productName,
      productInformation.productKey,
      productInformation.productVersion,
    ]
  );

  return (
    <ErrorContext.Provider
      value={{
        error,
        setError: setErrorAndScroll,
      }}
    >
      {children}
    </ErrorContext.Provider>
  );
};
